(function () {
  "use strict";

  // Mirrors docs/retailers.md and docs/categories.md — there's no CMS in v1 (see handoff
  // section 1), so this small registry is the front end's only source for display names and
  // the "is_chain" default-visibility rule. Keep it in sync by hand when those docs change.
  var RETAILERS = {
    coop: { name: "Co-op", isChain: true },
  };
  var CATEGORIES = {
    convenience: { name: "Convenience" },
  };

  var MAX_NEARBY_MILES = 50;
  var FULL_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
  var OUTWARD_CODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?$/i;

  function escapeHtml(str) {
    if (str == null) return "";
    return String(str).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function distanceMiles(fromLngLat, toLngLat) {
    var from = turf.point(fromLngLat);
    var to = turf.point(toLngLat);
    return turf.distance(from, to, { units: "miles" });
  }

  function StoreLocator(root) {
    this.root = root;
    this.config = {
      token: root.dataset.mapboxToken || "",
      style: root.dataset.mapboxStyle || "mapbox://styles/mapbox/light-v11",
      geojsonUrl: root.dataset.geojsonUrl,
      pinIconUrl: root.dataset.pinIconUrl || "",
      fallbackShopUrl: root.dataset.fallbackShopUrl || "",
      hideChainsByDefault: root.dataset.hideChainsByDefault === "true",
      initialZoom: Number(root.dataset.initialZoom) || 5,
    };
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.mapEl = root.querySelector("[data-map]");
    this.listEl = root.querySelector("[data-stockist-list]");
    this.countEl = root.querySelector("[data-result-count]");
    this.emptyEl = root.querySelector("[data-empty-state]");
    this.loadingEl = root.querySelector("[data-loading-state]");
    this.errorEl = root.querySelector("[data-error-state]");
    this.categoryFieldset = root.querySelector("[data-category-filter]");
    this.retailerFieldset = root.querySelector("[data-retailer-filter]");
    this.postcodeForm = root.querySelector("[data-postcode-form]");
    this.postcodeInput = root.querySelector("[data-postcode-input]");
    this.postcodeStatus = root.querySelector("[data-postcode-status]");

    this.allFeatures = [];
    this.activeCategories = new Set();
    this.activeRetailers = new Set();
    this.searchOrigin = null;
    this.map = null;
    this.popup = null;
    this.selectedId = null;

    this.init();
  }

  StoreLocator.prototype.init = async function () {
    try {
      var res = await fetch(this.config.geojsonUrl);
      if (!res.ok) throw new Error(res.status + " " + res.statusText);
      var fc = await res.json();
      this.allFeatures = fc.features || [];
    } catch (err) {
      this.hideLoading();
      this.showError("Could not load the stockist list. Please try again shortly.");
      console.error("[store-locator] failed to load GeoJSON", err);
      return;
    }

    this.buildFilterOptions();
    this.applyDefaultFilters();
    this.bindFilterEvents();
    this.bindPostcodeSearch();
    this.renderList(this.getFilteredFeatures());
    this.hideLoading();

    if (!this.config.token) {
      this.showError("Map is not configured yet — the stockist list above is still up to date.");
      return;
    }

    this.setUpMap();

    var params = new URLSearchParams(window.location.search);
    var postcodeParam = params.get("postcode");
    if (postcodeParam) {
      this.postcodeInput.value = postcodeParam;
      this.searchPostcode(postcodeParam);
    }
  };

  StoreLocator.prototype.setUpMap = function () {
    var self = this;
    mapboxgl.accessToken = this.config.token;
    this.map = new mapboxgl.Map({
      container: this.mapEl,
      style: this.config.style,
      center: [-2.5, 54.5],
      zoom: this.config.initialZoom,
    });
    this.map.addControl(new mapboxgl.NavigationControl(), "top-right");
    this.map.addControl(
      new mapboxgl.AttributionControl({ compact: true }),
      "bottom-right"
    );

    this.map.on("load", function () {
      self.map.addSource("stockists", {
        type: "geojson",
        data: self.toFeatureCollection(self.getFilteredFeatures()),
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
        promoteId: "id",
      });

      self.map.addLayer({
        id: "clusters",
        type: "circle",
        source: "stockists",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#4a4a3a",
          "circle-radius": ["step", ["get", "point_count"], 16, 10, 20, 50, 26],
          "circle-opacity": 0.85,
        },
      });

      self.map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "stockists",
        filter: ["has", "point_count"],
        layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": 12 },
        paint: { "text-color": "#ffffff" },
      });

      self.addUnclusteredLayer();
      self.bindMapEvents();
      self.fitToFeatures(self.getFilteredFeatures());
      self.onViewportChange();
    });
  };

  StoreLocator.prototype.addUnclusteredLayer = function () {
    var self = this;
    if (!this.config.pinIconUrl) {
      this.map.addLayer({
        id: "unclustered-point",
        type: "circle",
        source: "stockists",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": "#8a2f23",
          "circle-radius": ["case", ["boolean", ["feature-state", "selected"], false], 10, 7],
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
      });
      return;
    }
    this.map.loadImage(this.config.pinIconUrl, function (error, image) {
      if (error || !image) {
        console.error("[store-locator] pin icon failed to load, falling back to circle marker", error);
        self.config.pinIconUrl = "";
        self.addUnclusteredLayer();
        return;
      }
      if (!self.map.hasImage("stockist-pin")) self.map.addImage("stockist-pin", image);
      self.map.addLayer({
        id: "unclustered-point",
        type: "symbol",
        source: "stockists",
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": "stockist-pin",
          "icon-size": ["case", ["boolean", ["feature-state", "selected"], false], 0.65, 0.5],
          "icon-allow-overlap": true,
          "icon-anchor": "bottom",
        },
      });
    });
  };

  StoreLocator.prototype.bindMapEvents = function () {
    var self = this;

    this.map.on("click", "clusters", function (e) {
      var features = self.map.queryRenderedFeatures(e.point, { layers: ["clusters"] });
      var clusterId = features[0].properties.cluster_id;
      self.map.getSource("stockists").getClusterExpansionZoom(clusterId, function (err, zoom) {
        if (err) return;
        self.flyTo(features[0].geometry.coordinates, zoom);
      });
    });

    this.map.on("click", "unclustered-point", function (e) {
      var feature = e.features[0];
      self.selectFeature(feature, { flyTo: false, scrollList: true });
    });

    ["clusters", "unclustered-point"].forEach(function (layer) {
      self.map.on("mouseenter", layer, function () {
        self.map.getCanvas().style.cursor = "pointer";
      });
      self.map.on("mouseleave", layer, function () {
        self.map.getCanvas().style.cursor = "";
      });
    });

    this.map.on("moveend", function () {
      self.onViewportChange();
    });
  };

  StoreLocator.prototype.toFeatureCollection = function (features) {
    return { type: "FeatureCollection", features: features };
  };

  StoreLocator.prototype.getFilteredFeatures = function () {
    var self = this;
    return this.allFeatures.filter(function (f) {
      var p = f.properties;
      if (!self.activeCategories.has(p.category)) return false;
      var retailerKey = p.retailer || "__independent";
      if (!self.activeRetailers.has(retailerKey)) return false;
      return true;
    });
  };

  StoreLocator.prototype.buildCheckbox = function (group, value, label, checked) {
    var wrap = document.createElement("label");
    wrap.className = "store-locator__filter-option";
    var input = document.createElement("input");
    input.type = "checkbox";
    input.name = group;
    input.value = value;
    input.checked = checked;
    wrap.appendChild(input);
    wrap.appendChild(document.createTextNode(label));
    return wrap;
  };

  StoreLocator.prototype.buildFilterOptions = function () {
    var self = this;
    var categories = new Set(this.allFeatures.map(function (f) { return f.properties.category; }));
    var retailers = new Set(
      this.allFeatures.map(function (f) { return f.properties.retailer || "__independent"; })
    );

    if (this.categoryFieldset) {
      categories.forEach(function (slug) {
        var label = (CATEGORIES[slug] && CATEGORIES[slug].name) || slug;
        self.categoryFieldset.appendChild(self.buildCheckbox("category", slug, label, true));
      });
    }

    if (this.retailerFieldset) {
      if (retailers.has("__independent")) {
        this.retailerFieldset.appendChild(
          this.buildCheckbox("retailer", "__independent", "Independent stores", true)
        );
      }
      retailers.forEach(function (slug) {
        if (slug === "__independent") return;
        var meta = RETAILERS[slug] || { name: slug, isChain: false };
        var checked = !(meta.isChain && self.config.hideChainsByDefault);
        self.retailerFieldset.appendChild(self.buildCheckbox("retailer", slug, meta.name, checked));
      });
    }
  };

  StoreLocator.prototype.applyDefaultFilters = function () {
    if (this.categoryFieldset) {
      this.categoryFieldset.querySelectorAll("input[type=checkbox]:checked").forEach(
        function (cb) { this.activeCategories.add(cb.value); }.bind(this)
      );
    }
    if (this.retailerFieldset) {
      this.retailerFieldset.querySelectorAll("input[type=checkbox]:checked").forEach(
        function (cb) { this.activeRetailers.add(cb.value); }.bind(this)
      );
    }
  };

  StoreLocator.prototype.bindFilterEvents = function () {
    var self = this;
    [this.categoryFieldset, this.retailerFieldset].forEach(function (fieldset) {
      if (!fieldset) return;
      fieldset.addEventListener("change", function () {
        self.activeCategories = self.categoryFieldset
          ? new Set(
              [].slice.call(self.categoryFieldset.querySelectorAll("input:checked")).map(function (cb) {
                return cb.value;
              })
            )
          : new Set();
        self.activeRetailers = self.retailerFieldset
          ? new Set(
              [].slice.call(self.retailerFieldset.querySelectorAll("input:checked")).map(function (cb) {
                return cb.value;
              })
            )
          : new Set();
        self.refresh();
      });
    });
  };

  StoreLocator.prototype.refresh = function () {
    var filtered = this.getFilteredFeatures();
    if (this.map && this.map.getSource("stockists")) {
      this.map.getSource("stockists").setData(this.toFeatureCollection(filtered));
    }
    this.renderList(this.map ? this.viewportFilter(filtered) : filtered);
  };

  StoreLocator.prototype.onViewportChange = function () {
    this.renderList(this.viewportFilter(this.getFilteredFeatures()));
  };

  StoreLocator.prototype.viewportFilter = function (features) {
    if (!this.map) return features;
    var bounds = this.map.getBounds();
    return features.filter(function (f) {
      return bounds.contains(f.geometry.coordinates);
    });
  };

  StoreLocator.prototype.renderList = function (features) {
    var self = this;
    var items = features.map(function (f) {
      return {
        feature: f,
        miles: self.searchOrigin
          ? distanceMiles([self.searchOrigin.lon, self.searchOrigin.lat], f.geometry.coordinates)
          : null,
      };
    });

    items.sort(function (a, b) {
      if (self.searchOrigin) return a.miles - b.miles;
      return a.feature.properties.name.localeCompare(b.feature.properties.name);
    });

    this.listEl.innerHTML = "";
    items.forEach(function (item) {
      var feature = item.feature;
      var p = feature.properties;
      var li = document.createElement("li");
      li.className = "store-locator__list-item";
      li.dataset.stockistId = p.id;
      if (p.id === self.selectedId) li.classList.add("is-active");

      var button = document.createElement("button");
      button.type = "button";
      button.className = "store-locator__list-button";

      var name = document.createElement("span");
      name.className = "store-locator__list-name";
      name.textContent = p.name;
      button.appendChild(name);

      var address = document.createElement("span");
      address.className = "store-locator__list-address";
      address.textContent = p.address + ", " + p.city;
      button.appendChild(address);

      if (item.miles !== null) {
        var distance = document.createElement("span");
        distance.className = "store-locator__list-distance";
        distance.textContent = item.miles.toFixed(1) + " miles";
        button.appendChild(distance);
      }

      button.addEventListener("click", function () {
        self.selectFeature(feature, { flyTo: true, scrollList: false });
      });

      li.appendChild(button);
      self.listEl.appendChild(li);
    });

    var count = items.length;
    this.countEl.textContent = this.map
      ? count + " stockist" + (count === 1 ? "" : "s") + " in view"
      : count + " stockist" + (count === 1 ? "" : "s");
    this.emptyEl.hidden = count !== 0;
  };

  StoreLocator.prototype.selectFeature = function (feature, opts) {
    var p = feature.properties;
    if (this.map) {
      if (this.selectedId != null) {
        this.map.setFeatureState({ source: "stockists", id: this.selectedId }, { selected: false });
      }
      this.map.setFeatureState({ source: "stockists", id: p.id }, { selected: true });
    }
    this.selectedId = p.id;

    if (opts.flyTo) this.flyTo(feature.geometry.coordinates, 14);
    this.openPopup(feature);
    this.highlightListItem(p.id);
    if (opts.scrollList) this.scrollListItemIntoView(p.id);
  };

  StoreLocator.prototype.highlightListItem = function (id) {
    this.listEl.querySelectorAll(".store-locator__list-item").forEach(function (li) {
      li.classList.toggle("is-active", li.dataset.stockistId === id);
    });
  };

  StoreLocator.prototype.scrollListItemIntoView = function (id) {
    var el = this.listEl.querySelector('[data-stockist-id="' + CSS.escape(id) + '"]');
    if (el) el.scrollIntoView({ block: "nearest", behavior: this.reducedMotion ? "auto" : "smooth" });
  };

  StoreLocator.prototype.flyTo = function (coords, zoom) {
    if (!this.map) return;
    var opts = { center: coords, zoom: zoom || this.map.getZoom() };
    if (this.reducedMotion) this.map.jumpTo(opts);
    else this.map.flyTo(opts);
  };

  StoreLocator.prototype.fitToFeatures = function (features) {
    if (!features.length || !this.map) return;
    var bounds = new mapboxgl.LngLatBounds();
    features.forEach(function (f) { bounds.extend(f.geometry.coordinates); });
    this.map.fitBounds(bounds, { padding: 40, animate: !this.reducedMotion, maxZoom: 12 });
  };

  StoreLocator.prototype.openPopup = function (feature) {
    if (this.popup) this.popup.remove();
    var p = feature.properties;
    var html =
      "<strong>" + escapeHtml(p.name) + "</strong><br>" +
      escapeHtml(p.address) + "<br>" +
      escapeHtml(p.city) + ", " + escapeHtml(p.postcode);
    if (p.website) {
      html += '<br><a href="' + escapeHtml(p.website) + '" target="_blank" rel="noopener">Visit website</a>';
    }
    this.popup = new mapboxgl.Popup({ offset: 12 }).setLngLat(feature.geometry.coordinates).setHTML(html).addTo(this.map);
  };

  StoreLocator.prototype.bindPostcodeSearch = function () {
    var self = this;
    if (!this.postcodeForm) return;
    this.postcodeForm.addEventListener("submit", function (e) {
      e.preventDefault();
      self.searchPostcode(self.postcodeInput.value);
    });
  };

  StoreLocator.prototype.setPostcodeStatus = function (text) {
    if (!this.postcodeStatus) return;
    this.postcodeStatus.innerHTML = "";
    this.postcodeStatus.textContent = text;
  };

  StoreLocator.prototype.showNoNearbyStockists = function (postcode) {
    if (!this.postcodeStatus) return;
    this.postcodeStatus.innerHTML = "";
    this.postcodeStatus.appendChild(
      document.createTextNode(
        "No stockists within " + MAX_NEARBY_MILES + " miles of " + postcode + " yet. "
      )
    );
    if (this.config.fallbackShopUrl) {
      var link = document.createElement("a");
      link.href = this.config.fallbackShopUrl;
      link.textContent = "Shop online instead";
      this.postcodeStatus.appendChild(link);
    }
  };

  StoreLocator.prototype.searchPostcode = async function (rawValue) {
    var value = (rawValue || "").trim();
    var self = this;

    var endpoint;
    if (FULL_POSTCODE_RE.test(value)) {
      endpoint = "https://api.postcodes.io/postcodes/" + encodeURIComponent(value);
    } else if (OUTWARD_CODE_RE.test(value)) {
      endpoint = "https://api.postcodes.io/outcodes/" + encodeURIComponent(value);
    } else {
      this.setPostcodeStatus('That doesn’t look like a UK postcode — try "YO23 1JJ" or just "YO23".');
      return;
    }

    this.setPostcodeStatus("Searching…");

    var result;
    try {
      var res = await fetch(endpoint);
      if (res.status === 404) {
        this.setPostcodeStatus('We couldn’t find "' + value + '" — check it’s a valid UK postcode.');
        return;
      }
      if (!res.ok) throw new Error(res.status + " " + res.statusText);
      var body = await res.json();
      result = body.result;
    } catch (err) {
      console.error("[store-locator] postcode lookup failed", err);
      this.setPostcodeStatus("Postcode lookup is unavailable right now — please try again shortly.");
      return;
    }

    this.searchOrigin = {
      lon: result.longitude,
      lat: result.latitude,
      postcode: result.postcode || result.outcode,
    };

    var url = new URL(window.location.href);
    url.searchParams.set("postcode", this.searchOrigin.postcode);
    window.history.replaceState({}, "", url);

    var filtered = this.getFilteredFeatures();
    var nearest = filtered
      .map(function (f) {
        return { feature: f, miles: distanceMiles([self.searchOrigin.lon, self.searchOrigin.lat], f.geometry.coordinates) };
      })
      .sort(function (a, b) { return a.miles - b.miles; })[0];

    if (!nearest || nearest.miles > MAX_NEARBY_MILES) {
      this.showNoNearbyStockists(this.searchOrigin.postcode);
      this.renderList([]);
      if (this.map) this.flyTo([this.searchOrigin.lon, this.searchOrigin.lat], 9);
      return;
    }

    this.setPostcodeStatus("Showing stockists nearest to " + this.searchOrigin.postcode + ".");
    this.renderList(filtered);
    if (this.map) this.flyTo(nearest.feature.geometry.coordinates, 12);
  };

  StoreLocator.prototype.hideLoading = function () {
    if (this.loadingEl) this.loadingEl.hidden = true;
  };

  StoreLocator.prototype.showError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.hidden = false;
    this.errorEl.textContent = message;
  };

  function initAll() {
    document.querySelectorAll("[data-store-locator]").forEach(function (root) {
      if (root.dataset.storeLocatorInitialized) return;
      root.dataset.storeLocatorInitialized = "true";
      new StoreLocator(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", function (e) {
    var root = e.target.querySelector("[data-store-locator]");
    if (root) {
      root.dataset.storeLocatorInitialized = "";
      initAll();
    }
  });
})();
